/**
 * Collapse an arbitrary recorded sex/gender string to the body model's
 * two-value domain.
 *
 * The human body SVG only ships `male` and `female` artwork, so every render
 * site must reduce the patient's sex/gender to one of those. Female spellings
 * are matched case- and space-insensitively; everything else — including
 * unknown, blank, or unrecognised values — defaults to `male`, matching the
 * artwork the model already falls back to.
 */
export function normaliseBodyGender(value?: string | null): 'male' | 'female' {
	const normalised = (value ?? '').trim().toLowerCase();
	if (['f', 'female', 'woman', 'w'].includes(normalised)) {
		return 'female';
	}
	return 'male';
}

/**
 * Resolve the body-model gender for a patient, preferring `sex` and falling
 * back to `gender`.
 *
 * Uses `||` rather than `??` deliberately: `sex` is typed as a plain `string`
 * and is `''` (empty, not null/undefined) on `defaultPatient`, so `??` would
 * never fall through to `gender` — a blank `sex` would mask a populated
 * `gender`. `||` treats the empty string as absent, which is the intended
 * behaviour. This fixes a real bug.
 */
export function patientBodyGender(
	patient?: { sex?: string | null; gender?: string | null } | null,
): 'male' | 'female' {
	return normaliseBodyGender(patient?.sex || patient?.gender);
}
